import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterCustomizationDataDtoResponse } from '@asset-metrix/portal-gateway-client-js';
import { WidgetProps } from '@asset-metrix/portal-ui';

import { filterCustomizationQueryKey } from '../../api/portalGateway/tableFilterCustomization';
import {
  getDashboardDraftMoveAvailability,
  useDashboardEditModeStore,
  useIsDashboardDraftMode,
} from '../../store/states/dashboardEditMode';
import { queryClient } from '../../utils/queryClient';

/**
 * Filter customization is stored per tile id. Copy the loaded column ids onto
 * the new draft tile. A tile created in this draft already keeps those ids
 * locally, so a further copy uses that list. Nothing is written until save.
 */
function duplicateDashboardContent(sourceInstanceId: string) {
  const sourceTile = useDashboardEditModeStore
    .getState()
    .draftContents?.find((tile) => tile.instanceId === sourceInstanceId);
  const cached =
    queryClient.getQueryData<FilterCustomizationDataDtoResponse | null>(
      filterCustomizationQueryKey('content', sourceInstanceId)
    );
  const columnIds =
    sourceTile?.draftCustomFilterColumnIds ??
    cached?.data.columns.map((column) => column.columnId) ??
    [];
  useDashboardEditModeStore
    .getState()
    .duplicateContent(sourceInstanceId, columnIds);
}

function isCustomFilterAction(
  action: NonNullable<WidgetProps['actions']>[number]
): boolean {
  return action.icon.name === 'filter' || action.icon.name === 'filter-active';
}

/**
 * Dashboard view mode hides the custom-filter icon so the saved selections
 * stay a fixed default. Edit layout keeps that icon first, then the layout
 * actions. Other pages keep their actions unchanged.
 */
export function useDashboardContentEditActions(
  dashboardId: string | null | undefined,
  dashboardContentId: string,
  defaultActions: WidgetProps['actions']
): WidgetProps['actions'] {
  const { t } = useTranslation();
  const isDraftMode = useIsDashboardDraftMode(dashboardId);
  const draftContents = useDashboardEditModeStore((state) =>
    state.editDashboardId === dashboardId ? state.draftContents : null
  );
  const moveContent = useDashboardEditModeStore((state) => state.moveContent);
  const toggleContentWidth = useDashboardEditModeStore(
    (state) => state.toggleContentWidth
  );
  const removeContent = useDashboardEditModeStore(
    (state) => state.removeContent
  );
  const isSaving = useDashboardEditModeStore((state) => state.isSaving);
  const tileFiltersLoading = useDashboardEditModeStore(
    (state) => (state.tileFiltersPendingCounts[dashboardContentId] ?? 0) > 0
  );

  const moveAvailability = useMemo(
    () =>
      draftContents != null
        ? getDashboardDraftMoveAvailability(draftContents, dashboardContentId)
        : { canMoveUp: false, canMoveDown: false },
    [draftContents, dashboardContentId]
  );

  return useMemo(() => {
    if (dashboardId == null) {
      return defaultActions;
    }

    const filterAction = defaultActions?.find(isCustomFilterAction);

    if (!isDraftMode) {
      if (filterAction == null || defaultActions == null) {
        return defaultActions;
      }
      return defaultActions.filter((action) => action !== filterAction);
    }

    const layoutActions: NonNullable<WidgetProps['actions']> = [
      {
        title: t('app.widget.action.label.moveContentUp'),
        icon: { name: 'arrow_upward' },
        cb: () => moveContent(dashboardContentId, 'up'),
        disabled: isSaving || !moveAvailability.canMoveUp,
      },
      {
        title: t('app.widget.action.label.moveContentDown'),
        icon: { name: 'arrow_downward' },
        cb: () => moveContent(dashboardContentId, 'down'),
        disabled: isSaving || !moveAvailability.canMoveDown,
      },
      {
        title: t('app.widget.action.label.toggleContentWidth'),
        icon: { name: 'fit_screen' },
        cb: () => toggleContentWidth(dashboardContentId),
        disabled: isSaving,
      },
      {
        title: t('app.widget.action.label.duplicateContent'),
        icon: { name: 'content_copy' },
        cb: () => {
          void duplicateDashboardContent(dashboardContentId);
        },
        disabled: isSaving || tileFiltersLoading,
      },
      {
        title: t('app.widget.action.label.removeContent'),
        icon: { name: 'delete' },
        cb: () => removeContent(dashboardContentId),
        disabled: isSaving,
      },
    ];

    if (filterAction == null) {
      return layoutActions;
    }

    return [
      {
        ...filterAction,
        separatorAfter: true,
        disabled: isSaving || filterAction.disabled,
      },
      ...layoutActions,
    ];
  }, [
    dashboardId,
    defaultActions,
    isDraftMode,
    isSaving,
    moveAvailability.canMoveDown,
    moveAvailability.canMoveUp,
    moveContent,
    dashboardContentId,
    removeContent,
    t,
    tileFiltersLoading,
    toggleContentWidth,
  ]);
}
